import { useState } from "react";
import { CatalogPicker } from "@core/components/CatalogPicker";
import { List, ListRow } from "@core/components/List";
import { Value, ValueRow } from "@core/components/Value";
import { kindLabels, page, useSm, type InventoryView, type LegendaryPowerDef, type LegendaryPowerParam } from "./view";

/** A legendary power as stored at the piece: one row per choice. */
export interface PowerChoice {
  id: string;
  param: string | null;
  detail: string | null;
}

/** The choices of a power that the editor shows as one field: options as a list, free text otherwise. */
export function ParamField({ param, value, onChange, label }: { param: { label: string; options: { value: string; label: string }[] | null; open?: boolean }; value: string | null; onChange: (value: string | null) => void; label: string }) {
  if (param.options && !param.open) {
    return (
      <label className="field">
        <span>{param.label}</span>
        <select aria-label={`${label}: ${param.label}`} value={value ?? ""} onChange={(e) => onChange(e.target.value || null)}>
          <option value="">nicht gewählt</option>
          {param.options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
          {value && !param.options.some((o) => o.value === value) && <option value={value}>{value}</option>}
        </select>
      </label>
    );
  }

  return (
    <label className="field">
      <span>{param.label}</span>
      <input aria-label={`${label}: ${param.label}`} value={value ?? ""} list={param.options ? `${label}-options` : undefined} onChange={(e) => onChange(e.target.value || null)} />
      {param.options && (
        <datalist id={`${label}-options`}>
          {param.options.map((o) => (
            <option key={o.value} value={o.label} />
          ))}
        </datalist>
      )}
    </label>
  );
}

/** The second choice of a power: for Stärke the own parameter of the chosen strength, otherwise the detail of the power. */
function detailOf(def: LegendaryPowerDef, choice: PowerChoice): LegendaryPowerParam | { label: string; options: { value: string; label: string }[] | null; open: boolean } | null {
  if (!def.grantsStrength) return def.detail;
  const strength = def.parameter?.options?.find((o) => o.value === choice.param);
  if (!strength?.param) return null;
  return { label: strength.param.label, options: strength.param.options?.map((o) => ({ value: o, label: o })) ?? null, open: false };
}

/**
 * The legendary powers of a relic (MSK p. 100–103): chosen from the catalog, each with its choices; they cost relic points from the
 * quality those pay, not from bought quality (SM-G-098). The values of saved powers show with their derivation.
 */
export function RelicPowers({
  item,
  kinds,
  powers,
  onChange,
  changed,
  total,
  isRelic,
}: {
  item: InventoryView;
  kinds: string[];
  powers: PowerChoice[];
  onChange: (powers: PowerChoice[]) => void;
  changed: boolean;
  /** Relic points of the powers from the engine, for the draft while editing. */
  total: number | null;
  isRelic: boolean;
}) {
  const { view } = useSm();
  const [picking, setPicking] = useState(false);
  const defs = new Map(view.legendaryPowers.map((d) => [d.id, d]));
  // Costs come from the engine: the saved rows carry theirs, a changed list shows the price of the catalog until it is saved.
  const costText = (choice: PowerChoice, index: number) => {
    const saved = changed ? undefined : item.legendaryPowers[index];
    if (saved) return saved.free ? "frei" : `${saved.cost} P`;
    const def = defs.get(choice.id);
    return def?.costByStrength ? "1 P je Stufe" : `${def?.cost ?? "?"} P`;
  };
  const fits = (def: LegendaryPowerDef) => def.appliesTo.length === 0 || def.appliesTo.some((a) => kinds.includes(a));
  const update = (index: number, change: Partial<PowerChoice>) => onChange(powers.map((p, i) => (i === index ? { ...p, ...change } : p)));
  return (
    <section className="editor-block stack small-gap" aria-label="Legendäre Kräfte">
      <div className="row spread">
        <span>
          <strong>Legendäre Kräfte</strong>{" "}
          <span className="muted small">
            {changed ? total ?? "…" : <Value valueKey={item.powerCostKey} />} Reliktpunkte aus der Qualität, die die Reliktpunkte bezahlen
          </span>
        </span>
      </div>
      {!isRelic && <p className="muted small">Der Gegenstand ist kein Relikt; die Kräfte wirken nicht. Ordne ihn einem Relikt zu oder entferne sie (Mondstahlklingen S. 102).</p>}
      {!view.legendaryPowersAllowed && <p className="muted small">Nach den Regeloptionen dieses Charakters wirken Legendäre Kräfte jetzt nicht (Mondstahlklingen S. 100).</p>}
      <List aria-label="Gewählte Legendäre Kräfte">
        {powers.length === 0 && <li className="list-empty">Noch keine Legendäre Kraft gewählt.</li>}
        {powers.map((choice, index) => {
          const def = defs.get(choice.id);
          const detail = def ? detailOf(def, choice) : null;
          const saved = changed ? undefined : item.legendaryPowers[index];
          return (
            <ListRow
              key={`${choice.id}-${index}`}
              title={def?.name ?? choice.id}
              value={costText(choice, index)}
              actions={
                <button type="button" className="link danger" onClick={() => onChange(powers.filter((_, i) => i !== index))}>
                  Entfernen
                </button>
              }
              below={
                def && (def.parameter || detail || (saved?.values.length ?? 0) > 0) ? (
                  <div className="stack small-gap">
                    {def.parameter && <ParamField param={def.parameter} value={choice.param} label={def.name} onChange={(param) => update(index, { param, detail: def.grantsStrength ? null : choice.detail })} />}
                    {detail && <ParamField param={detail} value={choice.detail} label={`${def.name} ${detail.label}`} onChange={(value) => update(index, { detail: value })} />}
                    {saved?.values.map((v) => <ValueRow key={v.key} valueKey={v.key} label={v.unit ? `${v.label} (${v.unit})` : v.label} />)}
                    {def.summary && <span className="muted small">{def.summary} ({page(def.source)})</span>}
                  </div>
                ) : def?.summary ? (
                  <span className="muted small">
                    {def.summary} ({page(def.source)})
                  </span>
                ) : undefined
              }
            />
          );
        })}
      </List>
      {isRelic && (
        <div className="actions">
          <button type="button" className="button secondary" onClick={() => setPicking(true)}>
            + Legendäre Kraft wählen
          </button>
        </div>
      )}
      {picking && (
        <CatalogPicker
          type="legendary-powers"
          title="Legendäre Kraft"
          hint="Der Wert rechts sind die Reliktpunkte, die eine Kraft kostet (Mondstahlklingen S. 102)."
          unavailable={(p) => {
            const def = defs.get(p.id);
            if (!def) return null;
            if (!fits(def)) return `nicht für ${kindLabels[item.kind]}`;
            if (powers.filter((c) => c.id === def.id).length >= def.repeatable) return def.repeatable === 1 ? "schon gewählt" : `schon ${def.repeatable}-mal gewählt`;
            if (def.exclusiveGroup && powers.some((c) => c.id !== def.id && defs.get(c.id)?.exclusiveGroup === def.exclusiveGroup)) return "andere Variante gewählt";
            return null;
          }}
          onClose={() => setPicking(false)}
          onPick={(p) => {
            setPicking(false);
            onChange([...powers, { id: p.id, param: null, detail: null }]);
          }}
        />
      )}
    </section>
  );
}
